import React, { useEffect, useRef, useState } from 'react';
import { Input } from 'antd';
import useDebounce from './useDebounce';
import Select from './select';
import { ValueDriverNumberInput } from '@components/index';

const OTHER_TEXT_PREFIX = 'Other-';

// @param ValueDriverSekect.Select.value Handle prefix if exist, optional
const getSelectValue = (_value) => {
  if (!_value) {
    return _value;
  }

  if (_value === OTHER_TEXT_PREFIX) {
    return null;
  }

  return _value.replace(OTHER_TEXT_PREFIX, '');
};

// @func useEffect[]
const stockOtherValue = (ref, _value) => {
  if (_value?.startsWith(OTHER_TEXT_PREFIX)) {
    ref.current = _value;
  }
};

const renderDropdown =
  ({
    showInput,
    currentValue,
    otherText,
    changeOtherText,
    getOtherInputValue,
    openDropDown,
    handleOtherInputBlur,
    handleSelect,
    inputNumber,
  }) =>
  (selects) => {
    const InputComponent = inputNumber ? ValueDriverNumberInput : Input;
    const componentProps = inputNumber
      ? {
          onValueFormat: (value) => {
            changeOtherText(value);
          },
          precision: 0,
          min: 0,
          controls: false,
          onPressEnter: (e) => {
            const value = e.target.value;
            handleSelect(typeof value === 'string' && value ? value : null);
          },
        }
      : {
          onChange: (e) => changeOtherText(e.target.value),
          onPressEnter: (e) => {
            handleSelect(e.target.value);
          },
        };
    return (
      <div className="select-option-input">
        {selects}
        {showInput ? (
          <InputComponent
            autoFocus
            value={getOtherInputValue(currentValue)}
            className="other-input"
            onFocus={openDropDown}
            onBlur={handleOtherInputBlur}
            maxLength={64}
            {...componentProps}
          />
        ) : null}
      </div>
    );
  };

export default function ValueDriverSelect({
  optionKey,
  options = {},
  optionDependKey,
  optionResetKeys,
  value,
  onChange: triggerFormOnChange,
  formInstance: form,
  rowIndex,
  inputNumber,
  formChange = () => {},
  ...props
}) {
  const [currentValue, changeCurrentValue] = useState(value);
  const [preValue, changePreValue] = useState(value);
  const [currentOptions, changeCurrentOptions] = useState(options[optionKey]);
  const [dropDownOpen, toggleDropdown] = useState(false);
  const [otherText, changeOtherText] = useState('');
  const debouncedToggleDropdown = useDebounce(toggleDropdown, 100);
  // const debouncedChangeOtherText = useDebounce(changeOtherText, 200);
  const openDropDown = () => debouncedToggleDropdown(true);
  const closeDropDown = () => debouncedToggleDropdown(false);

  const otherValueStockRef = useRef();

  const showInput =
    currentValue?.toLowerCase?.().includes('#other#') ||
    currentValue?.startsWith?.(OTHER_TEXT_PREFIX);

  useEffect(() => {
    stockOtherValue(otherValueStockRef, value);
  }, []);

  useEffect(() => {
    if (
      value?.toLowerCase().includes('#other#') &&
      !value?.startsWith(OTHER_TEXT_PREFIX) &&
      value?.toLowerCase() !== 'other' &&
      otherValueStockRef.current
    ) {
      trigChange(otherValueStockRef.current);
    }
  }, [value]);

  useEffect(() => {
    if (!dropDownOpen) {
      return;
    }

    const newValue = otherText ? `${OTHER_TEXT_PREFIX}${otherText}` : null;
    trigChange(newValue);
  }, [otherText]);

  const trigChange = (value) => {
    changeCurrentValue(value);
    if (
      !value?.toLowerCase().includes('#other#') &&
      !value?.startsWith(OTHER_TEXT_PREFIX)
    ) {
      changePreValue(value);
    }
    triggerFormOnChange(value);
  };

  const debouncedTrigChange = useDebounce(trigChange, 100);

  const handleSelect = (newValue) => {
    formChange();
    if (
      newValue === currentValue ||
      (newValue?.toLowerCase().includes('#other#') &&
        currentValue?.startsWith(OTHER_TEXT_PREFIX))
    ) {
      return;
    }
    debouncedTrigChange(newValue);

    if (optionResetKeys) {
      const resetKeys = optionResetKeys.map((resetKey) => {
        const path = [...resetKey];
        path.splice(1, 0, rowIndex);
        return path;
      });
      form.resetFields(resetKeys);
    }

    if (!newValue?.toLowerCase().includes('#other#')) {
      toggleDropdown(false);
    }
  };

  const handleFilterOptions = (isOpen) => {
    if (isOpen && optionDependKey) {
      const dependPath = [...optionDependKey];
      dependPath.splice(1, 0, rowIndex);
      const dependValue = form.getFieldValue(dependPath);

      const newOpts = options[optionKey].filter((opt) =>
        opt.value.replace(dependValue, '')?.startsWith('-'),
      );

      changeCurrentOptions(newOpts);
    }
  };

  const handleSelectClick = (e) => {
    !e.target?.className.includes('ant-select-item') &&
      !e.target?.className.includes('other-input') &&
      !e.target?.className.includes('ant-input-number-input') &&
      debouncedToggleDropdown(!dropDownOpen);
  };

  const handleOtherInputBlur = () => {
    handleSelect(preValue);
  };

  // @param renderDropdown.Input.value
  const getOtherInputValue = (value) => {
    if (otherText != '') {
      return otherText;
    }
    if (!value.includes(OTHER_TEXT_PREFIX)) {
      return '';
    }

    return value.replace(OTHER_TEXT_PREFIX, '');
  };

  const dropdownProps = {
    showInput,
    currentValue,
    otherText,
    changeOtherText,
    getOtherInputValue,
    handleOtherInputBlur,
    openDropDown,
    handleSelect,
    inputNumber,
  };

  return (
    <Select
      {...props}
      className={inputNumber ? 'value-driver-select-number' : ''}
      dropdownRender={renderDropdown(dropdownProps)}
      open={dropDownOpen}
      onClick={handleSelectClick}
      onBlur={() => closeDropDown()}
      listHeight={500}
      value={getSelectValue(value)}
      defaultValue={currentValue}
      options={currentOptions}
      onSelect={handleSelect}
      onDropdownVisibleChange={handleFilterOptions}
    />
  );
}
